import React, { FC, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Prompt, useHistory } from 'react-router';

import { EvConfirm, EvSection, EvSpinner } from '@evinced-private/ui-common';

import { SITE_SCANNER_APP_ID } from 'src/consts/dom-consts';
import { RoutesHelper } from 'src/helpers/RoutesHelper';
import { isCrawlCurrentlyRunning } from 'src/services/PropertiesService';
import { SETTINGS_SECTION_TYPES } from 'src/types/SettingsSectionTypes';

import { FileTypeSettingsV2 } from './components/FileTypeSettingsV2';
import { GroupingSettingsV2 } from './components/GroupingSettingsV2';
import { LoginSettingsV2 } from './components/LoginSettingsV2';
import { PropertySettingsActionsV2 } from './components/PropertySettingsActionsV2';
import { PropertySettingsTitleV2 } from './components/PropertySettingsTitleV2';
import { SchedulingSettingsV2 } from './components/SchedulingSettingsV2';
import { ScopeSettingsV2 } from './components/ScopeSettingsV2';
import { SeedsSettingsV2 } from './components/SeedsSettingsV2';
import { useNavigationBlocking } from './hooks/useNavigationBlocking';
import { usePropertySave } from './hooks/usePropertySave';
import { usePropertySettingsEditor } from './hooks/usePropertySettingsEditor';
import { usePropertySourceMode } from './hooks/usePropertySourceMode';
import { useRouteRegistration } from './hooks/useRouteRegistration';
import { PropertySettingsEditorProps } from './types/PropertySettingsEditorProps';
import {
	PropertySettingsContextValue,
	PropertySettingsProvider,
	RegisterErrorRef
} from './context';
import { setLoading } from './state';

export const PropertySettingsEditor: FC<PropertySettingsEditorProps> = ({
	initialProperty,
	sourceMode: initialSourceMode,
	isNew,
	propertyId
}) => {
	const history = useHistory();

	const { state, dispatch, isDirty, isListMode } = usePropertySettingsEditor({
		initialProperty,
		sourceMode: initialSourceMode,
		isNew
	});

	const { shouldBlockNavigation, setShouldBlockNavigation } = useNavigationBlocking(isDirty);
	useRouteRegistration(propertyId);

	const setSourceMode = usePropertySourceMode({ dispatch });

	// Deferred navigation: Prompt unblocks in an effect cleanup after
	// setShouldBlockNavigation(false) re-renders. A direct history.push
	// in the same callback fires before that cleanup, so the still-active
	// Prompt intercepts it. Storing the path in state and pushing in a
	// separate effect guarantees the Prompt has unblocked first.
	const [navigateTo, setNavigateTo] = useState<string | null>(null);

	useEffect(() => {
		if (navigateTo) {
			history.push(navigateTo);
		}
	}, [navigateTo, history]);

	const goToProperty = useCallback(
		(targetPropertyId: string): void => {
			dispatch(setLoading(true));
			setShouldBlockNavigation(false);
			setNavigateTo(RoutesHelper.getPropertyPagePath(targetPropertyId));
		},
		[dispatch, setShouldBlockNavigation]
	);

	const errorRefs = useRef<Partial<Record<SETTINGS_SECTION_TYPES, HTMLElement>>>({});

	const registerErrorRef = useCallback<RegisterErrorRef>((sectionId, element) => {
		if (element) {
			errorRefs.current[sectionId] = element;
		} else {
			delete errorRefs.current[sectionId];
		}
	}, []);

	const save = usePropertySave({
		propertyId,
		dispatch,
		onSaved: goToProperty,
		errorRefs
	});

	const [isCrawlConfirmOpen, setCrawlConfirmOpen] = useState(false);

	const submitSettings = useCallback(
		async (shouldCrawl: boolean): Promise<void> => {
			if (!state.draft) {
				return;
			}

			if (shouldCrawl) {
				const isCrawlRunning = await isCrawlCurrentlyRunning(state.draft.id);
				if (isCrawlRunning) {
					setCrawlConfirmOpen(true);
					return;
				}
			}

			save({ draft: state.draft, isListMode, shouldCrawl });
		},
		[save, state.draft, isListMode]
	);

	const handleFormSubmit = useCallback(
		(e: FormEvent<HTMLFormElement>): void => {
			e.preventDefault();
			submitSettings(false);
		},
		[submitSettings]
	);

	const handleConfirmSaveAndCrawl = useCallback(
		(closeModal: () => void): void => {
			if (state.draft) {
				save({ draft: state.draft, isListMode, shouldCrawl: true });
			}
			setCrawlConfirmOpen(false);
			closeModal();
		},
		[save, state.draft, isListMode]
	);

	const contextValue: PropertySettingsContextValue = useMemo(
		() => ({
			state,
			dispatch,
			isDirty,
			isListMode,
			isNew,
			propertyId,
			save,
			setSourceMode,
			goToProperty,
			submitSettings,
			registerErrorRef
		}),
		[
			state,
			dispatch,
			isDirty,
			isListMode,
			isNew,
			propertyId,
			save,
			setSourceMode,
			goToProperty,
			submitSettings,
			registerErrorRef
		]
	);

	if (state.isLoading || !state.draft) {
		return (
			<div className="spinner">
				<EvSpinner />
			</div>
		);
	}

	return (
		<PropertySettingsProvider value={contextValue}>
			<form className="property-settings-page" onSubmit={handleFormSubmit} noValidate>
				<Prompt
					when={shouldBlockNavigation}
					message="You have unsaved changes, are you sure you want to leave?"
				/>
				<EvConfirm
					appElement={SITE_SCANNER_APP_ID}
					title="Confirm Save and Crawl"
					approveButtonText="Confirm"
					promptMessage="Saving while a crawl is running will cancel the crawl, are you sure you want to save?"
					isOpen={isCrawlConfirmOpen}
					isControlled={true}
					onConfirm={handleConfirmSaveAndCrawl}
					onClose={() => setCrawlConfirmOpen(false)}
				/>
				<PropertySettingsTitleV2 />
				<EvSection
					className="property-settings-content"
					ariaLabel={isListMode ? 'Property list settings' : 'Property crawl settings'}
					skipLinkId={isListMode ? 'list-settings' : 'crawl-settings'}
				>
					<SeedsSettingsV2 />
					{!isListMode && (
						<>
							<ScopeSettingsV2 />
							<FileTypeSettingsV2 />
							<GroupingSettingsV2 />
						</>
					)}
					<LoginSettingsV2 />
					<SchedulingSettingsV2 />
				</EvSection>
				<PropertySettingsActionsV2 />
			</form>
		</PropertySettingsProvider>
	);
};
